import React, { useEffect, useState, createElement } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeftIcon, CheckCircle2Icon, HomeIcon, UserIcon, MapPinIcon, LoaderIcon, AlertCircleIcon, AppWindowIcon, LayersIcon, DoorOpenIcon } from 'lucide-react';
import { readUrlTrackingParams, readLeadTrackingValues } from './LeadTrackingScripts';
interface UniversalWindowsDirectQuoteFormProps {
  initialZip: string;
  serviceSlug: string;
  onClose: () => void;
}
export function UniversalWindowsDirectQuoteForm({
  initialZip,
  serviceSlug,
  onClose
}: UniversalWindowsDirectQuoteFormProps) {
  const [step, setStep] = useState(1);
  const [direction, setDirection] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [consentChecked, setConsentChecked] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [zipLookupLoading, setZipLookupLoading] = useState(false);
  const [zipLookupDone, setZipLookupDone] = useState(false);
  const [addressError, setAddressError] = useState('');
  const [geoError, setGeoError] = useState('');
  const [editingZip, setEditingZip] = useState(false);
  const getProductId = (slug: string): string => {
    switch (slug) {
      case 'windows':
        return 'Windows';
      case 'siding':
        return 'Siding';
      case 'gutters':
        return 'Gutters';
      case 'doors':
        return 'Doors';
      default:
        return 'Windows';
    }
  };
  const getServiceLabel = (slug: string): string => {
    switch (slug) {
      case 'windows':
        return 'Windows';
      case 'siding':
        return 'Siding';
      case 'gutters':
        return 'Gutters';
      case 'doors':
        return 'Doors';
      default:
        return 'Home Improvement';
    }
  };
  const [formData, setFormData] = useState({
    formName: 'UWD_form_v1',
    product_id: getProductId(serviceSlug),
    trustedform_cert_url: '',
    first_name: '',
    last_name: '',
    phone_1: '',
    email: '',
    address_1: '',
    city: '',
    state: '',
    postal_code: initialZip || '',
    project_details: '',
    homeOwnership: ''
  });
  // Zip code lookup
  useEffect(() => {
    const zip = formData.postal_code;
    if (zip && zip.length === 5) {
      setZipLookupLoading(true);
      setZipLookupDone(false);
      setGeoError('');
      fetch(`https://api.zippopotam.us/us/${zip}`).then(res => {
        if (!res.ok) throw new Error('Invalid zip');
        return res.json();
      }).then(data => {
        if (data.places && data.places.length > 0) {
          const place = data.places[0];
          setFormData(prev => ({
            ...prev,
            city: place['place name'] || '',
            state: place['state abbreviation'] || ''
          }));
          setZipLookupDone(true);
        }
      }).catch(() => {
        setGeoError('Please enter a valid US ZIP code');
        setZipLookupDone(false);
      }).finally(() => {
        setZipLookupLoading(false);
      });
    }
  }, [formData.postal_code]);
  // Phone formatting
  const formatPhoneNumber = (value: string): string => {
    const digits = value.replace(/\D/g, '').slice(0, 10);
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
    return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
  };
  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      phone_1: formatPhoneNumber(e.target.value)
    }));
  };
  // TrustedForm
  useEffect(() => {
    try {
      const tf = document.createElement('script');
      tf.type = 'text/javascript';
      tf.async = true;
      tf.src = 'https://api.trustedform.com/trustedform.js?provide_referrer=false&field=xxTrustedFormCertUrl&l=' + new Date().getTime() + Math.random();
      document.head.appendChild(tf);
    } catch (e) {
      console.warn('TrustedForm script could not be loaded:', e);
    }
    const interval = setInterval(() => {
      try {
        const certInput = document.querySelector('input[name="xxTrustedFormCertUrl"]') as HTMLInputElement;
        if (certInput && certInput.value) {
          setFormData(prev => ({
            ...prev,
            trustedform_cert_url: certInput.value
          }));
          clearInterval(interval);
        }
      } catch (e) {}
    }, 1000);
    return () => clearInterval(interval);
  }, []);
  const totalSteps = 4;
  const progress = step / totalSteps * 100;
  const handleNext = () => {
    setDirection(1);
    setStep(prev => Math.min(prev + 1, totalSteps));
  };
  const handleBack = () => {
    setDirection(-1);
    setStep(prev => Math.max(prev - 1, 1));
  };
  const handleOptionSelect = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    setTimeout(() => handleNext(), 300);
  };
  const validateAddress = (address: string): boolean => {
    return /^\d+\s+\S{2,}/.test(address.trim());
  };
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (name === 'address_1') setAddressError('');
  };
  const handleAddressBlur = () => {
    if (formData.address_1 && !validateAddress(formData.address_1)) {
      setAddressError('Please enter a valid street address (e.g. 123 Main St)');
    } else {
      setAddressError('');
    }
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (typeof window !== 'undefined') {
      window._mgq = window._mgq || [];
      window._mgq.push(['MgSensorInvoke', 'submission']);
    }
    setIsSubmitting(true);
    const tracking = readLeadTrackingValues();
    const finalCert = tracking.xxTrustedFormCertUrl || formData.trustedform_cert_url;
    const finalLeadId = tracking.leadid_token;
    const payload = {
      ...readUrlTrackingParams(),
      ...formData,
      xxTrustedFormCertUrl: finalCert,
      trustedform_cert_url: finalCert,
      leadid_token: finalLeadId
    };
    console.log('Submitting UWD Lead:', payload);
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1500));
    setIsSubmitting(false);
    setIsSuccess(true);
  };
  const variants = {
    enter: (direction: number) => ({
      x: direction > 0 ? 50 : -50,
      opacity: 0
    }),
    center: {
      x: 0,
      opacity: 1
    },
    exit: (direction: number) => ({
      x: direction < 0 ? 50 : -50,
      opacity: 0
    })
  };
  const Header = () => <header className="bg-white border-b border-gray-200 py-4 px-4 md:px-8 flex justify-between items-center sticky top-0 z-10" data-id="element-3447">
      <div className="flex items-center gap-2" data-id="element-3448">
        <span className="text-xl font-extrabold text-[#00529B] tracking-tight" data-id="element-3449">
          Universal Windows Direct
        </span>
      </div>
      <button onClick={onClose} className="text-sm font-medium text-gray-500 hover:text-gray-900 transition-colors flex items-center gap-1" data-id="element-3450">
        <ArrowLeftIcon className="w-4 h-4" data-id="element-3451" />
        <span className="hidden sm:inline" data-id="element-3452">Exit</span>
      </button>
    </header>;
  if (isSuccess) {
    return <div className="min-h-screen bg-gray-50 flex flex-col" data-id="element-3453">
        <Header data-id="element-3454" />
        <div className="flex-1 flex items-center justify-center p-6" data-id="element-3455">
          <motion.div initial={{
          scale: 0.9,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} className="bg-white rounded-2xl shadow-xl p-10 max-w-md w-full text-center border border-gray-100" data-id="element-3456">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-3457">
              <CheckCircle2Icon className="w-10 h-10 text-green-600" data-id="element-3458" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-3459">
              Quote Request Received!
            </h2>
            <p className="text-gray-600 mb-8" data-id="element-3460">
              Thank you, {formData.first_name}! A Universal Windows Direct
              specialist will contact you shortly at {formData.phone_1} to
              discuss your {getServiceLabel(serviceSlug).toLowerCase()} project
              in {formData.city}.
            </p>
            <button onClick={onClose} className="w-full h-14 bg-[#00529B] hover:bg-[#004080] text-white font-bold rounded-xl transition-colors" data-id="element-3461">
              Return to Home
            </button>
          </motion.div>
        </div>
      </div>;
  }
  return <div className="min-h-screen bg-gray-50 flex flex-col" data-id="element-3462">
      <Header data-id="element-3463" />
      <input type="hidden" name="xxTrustedFormCertUrl" data-id="element-3464" />

      <div className="w-full bg-gray-200 h-2" data-id="element-3465">
        <motion.div className="bg-[#00529B] h-full" initial={{
        width: 0
      }} animate={{
        width: `${progress}%`
      }} transition={{
        duration: 0.3,
        ease: 'easeInOut'
      }} data-id="element-3466" />
      </div>

      <main className="flex-1 flex flex-col items-center pt-8 pb-20 px-4" data-id="element-3467">
        <div className="w-full max-w-2xl" data-id="element-3468">
          <div className="mb-6 h-8" data-id="element-3469">
            {step > 1 && <button onClick={handleBack} className="text-gray-500 hover:text-gray-900 font-medium flex items-center gap-2 transition-colors" data-id="element-3470">
                <ArrowLeftIcon className="w-4 h-4" data-id="element-3471" /> Back
              </button>}
          </div>

          <div className="relative bg-white rounded-2xl shadow-lg p-6 md:p-10 border border-gray-100" data-id="element-3472">
            <AnimatePresence mode="wait" custom={direction} data-id="element-3473">
              {/* STEP 1: Project Details */}
              {step === 1 && <motion.div key="step1" custom={direction} variants={variants} initial="enter" animate="center" exit="exit" transition={{
              duration: 0.3
            }} className="w-full" data-id="element-3474">
                  <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-8 text-center" data-id="element-3475">
                    What best describes your{' '}
                    {getServiceLabel(serviceSlug).toLowerCase()} project?
                  </h1>
                  <div className="flex flex-col gap-4" data-id="element-3476">
                    {[{
                  value: 'Replace existing',
                  desc: `I need to replace my current ${getServiceLabel(serviceSlug).toLowerCase()}`
                }, {
                  value: 'New installation',
                  desc: `I need new ${getServiceLabel(serviceSlug).toLowerCase()} installed`
                }, {
                  value: 'Repair',
                  desc: `I need repairs on my current ${getServiceLabel(serviceSlug).toLowerCase()}`
                }, {
                  value: 'Not sure',
                  desc: "I'd like an expert opinion"
                }].map((option, idx) => <button key={idx} onClick={() => handleOptionSelect('project_details', option.value)} className={`p-5 rounded-xl border-2 text-left flex items-center gap-4 transition-all duration-200 ${formData.project_details === option.value ? 'border-[#00529B] bg-blue-50 shadow-md' : 'border-gray-200 bg-white hover:border-blue-300 hover:shadow-sm'}`} data-id="element-3477">
                        <div className={`p-3 rounded-full flex-shrink-0 ${formData.project_details === option.value ? 'bg-blue-100 text-[#00529B]' : 'bg-gray-50 text-gray-600'}`} data-id="element-3478">
                          {serviceSlug === 'windows' && <AppWindowIcon className="w-6 h-6" data-id="element-3479" />}
                          {serviceSlug === 'siding' && <LayersIcon className="w-6 h-6" data-id="element-3480" />}
                          {serviceSlug === 'doors' && <DoorOpenIcon className="w-6 h-6" data-id="element-3481" />}
                          {serviceSlug === 'gutters' && <HomeIcon className="w-6 h-6" data-id="element-3482" />}
                        </div>
                        <div data-id="element-3483">
                          <div className="font-bold text-gray-900 text-lg" data-id="element-3484">
                            {option.value}
                          </div>
                          <div className="text-gray-500 text-sm" data-id="element-3485">
                            {option.desc}
                          </div>
                        </div>
                      </button>)}
                  </div>
                </motion.div>}

              {/* STEP 2: Homeownership */}
              {step === 2 && <motion.div key="step2" custom={direction} variants={variants} initial="enter" animate="center" exit="exit" transition={{
              duration: 0.3
            }} className="w-full" data-id="element-3486">
                  <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-8 text-center" data-id="element-3487">
                    Do you own your home?
                  </h1>
                  <div className="flex flex-col gap-4" data-id="element-3488">
                    {[{
                  value: 'Yes',
                  desc: 'I own this property'
                }, {
                  value: 'No',
                  desc: 'I rent this property'
                }].map((option, idx) => <button key={idx} onClick={() => handleOptionSelect('homeOwnership', option.value)} className={`p-5 rounded-xl border-2 text-left flex items-center gap-4 transition-all duration-200 ${formData.homeOwnership === option.value ? 'border-[#00529B] bg-blue-50 shadow-md' : 'border-gray-200 bg-white hover:border-blue-300 hover:shadow-sm'}`} data-id="element-3489">
                        <div className={`p-3 rounded-full flex-shrink-0 ${formData.homeOwnership === option.value ? 'bg-blue-100 text-[#00529B]' : 'bg-gray-50 text-gray-600'}`} data-id="element-3490">
                          <UserIcon className="w-6 h-6" data-id="element-3491" />
                        </div>
                        <div data-id="element-3492">
                          <div className="font-bold text-gray-900 text-lg" data-id="element-3493">
                            {option.value}
                          </div>
                          <div className="text-gray-500 text-sm" data-id="element-3494">
                            {option.desc}
                          </div>
                        </div>
                      </button>)}
                  </div>
                </motion.div>}

              {/* STEP 3: Address */}
              {step === 3 && <motion.div key="step3" custom={direction} variants={variants} initial="enter" animate="center" exit="exit" transition={{
              duration: 0.3
            }} className="w-full" data-id="element-3495">
                  <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-8 text-center" data-id="element-3496">
                    Where is your project located?
                  </h1>
                  <div className="space-y-5" data-id="element-3497">
                    <div data-id="element-3498">
                      <label htmlFor="address_1" className="block text-sm font-bold text-gray-700 mb-1" data-id="element-3499">
                        Street Address
                      </label>
                      <input type="text" id="address_1" name="address_1" value={formData.address_1} onChange={handleInputChange} onBlur={handleAddressBlur} className={`w-full h-14 px-4 bg-gray-50 border rounded-xl text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#00529B] focus:border-transparent ${addressError ? 'border-red-400 bg-red-50' : 'border-gray-300'}`} placeholder="123 Main St" data-id="element-3500" />
                      {addressError && <p className="text-red-500 text-xs mt-1.5 font-medium" data-id="element-3501">
                          {addressError}
                        </p>}

                      <div className="mt-3 flex items-center gap-2" data-id="element-3502">
                        {zipLookupLoading ? <div className="flex items-center gap-2 text-gray-400 text-sm" data-id="element-3503">
                            <LoaderIcon className="w-4 h-4 animate-spin" data-id="element-3504" />
                            <span data-id="element-3505">Looking up location...</span>
                          </div> : editingZip ? <div className="flex items-center gap-2 w-full" data-id="element-3506">
                            <MapPinIcon className="w-4 h-4 text-[#00529B] flex-shrink-0" data-id="element-3507" />
                            <input type="text" value={formData.postal_code} onChange={e => {
                        const val = e.target.value.replace(/\D/g, '').slice(0, 5);
                        setFormData(prev => ({
                          ...prev,
                          postal_code: val,
                          city: '',
                          state: ''
                        }));
                        setZipLookupDone(false);
                        setGeoError('');
                      }} onBlur={() => {
                        if (formData.postal_code.length === 5) setEditingZip(false);
                      }} onKeyDown={e => {
                        if (e.key === 'Enter' && formData.postal_code.length === 5) setEditingZip(false);
                      }} placeholder="Enter ZIP" maxLength={5} autoFocus className={`flex-1 h-10 px-3 bg-white border rounded-lg text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-[#00529B] focus:border-transparent ${geoError ? 'border-red-400 text-red-700' : 'border-blue-300 text-[#00529B]'}`} data-id="element-3508" />
                          </div> : zipLookupDone && formData.city && formData.state ? <button type="button" onClick={() => setEditingZip(true)} className="flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 w-full hover:bg-blue-100 transition-colors cursor-pointer text-left" data-id="element-3509">
                            <MapPinIcon className="w-4 h-4 text-[#00529B] flex-shrink-0" data-id="element-3510" />
                            <span className="text-sm font-semibold text-[#00529B]" data-id="element-3511">
                              {formData.city}, {formData.state}{' '}
                              {formData.postal_code}
                            </span>
                            <span className="text-[10px] text-blue-400 ml-auto" data-id="element-3512">
                              edit
                            </span>
                          </button> : <button type="button" onClick={() => setEditingZip(true)} className={`flex items-center gap-2 bg-gray-50 border rounded-lg px-3 py-2 w-full hover:bg-gray-100 transition-colors cursor-pointer text-left ${geoError ? 'border-red-300' : 'border-gray-200'}`} data-id="element-3513">
                            <MapPinIcon className={`w-4 h-4 ${geoError ? 'text-red-400' : 'text-gray-400'}`} data-id="element-3514" />
                            <span className={`text-sm ${geoError ? 'text-red-500' : 'text-gray-500'}`} data-id="element-3515">
                              {geoError || (formData.postal_code ? `Zip: ${formData.postal_code}` : 'Enter your ZIP code')}
                            </span>
                          </button>}
                      </div>
                    </div>

                    <button onClick={() => {
                  if (!validateAddress(formData.address_1)) {
                    setAddressError('Please enter a valid street address (e.g. 123 Main St)');
                    return;
                  }
                  handleNext();
                }} disabled={!formData.address_1 || !formData.city || !formData.state || !formData.postal_code || !!addressError || !!geoError} className="w-full mt-6 h-14 bg-[#00529B] hover:bg-[#004080] text-white font-bold text-lg rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-3516">
                      Continue
                    </button>
                  </div>
                </motion.div>}

              {/* STEP 4: Contact Info */}
              {step === 4 && <motion.div key="step4" custom={direction} variants={variants} initial="enter" animate="center" exit="exit" transition={{
              duration: 0.3
            }} className="w-full" data-id="element-3517">
                  <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2 text-center" data-id="element-3518">
                    Who should we send the quote to?
                  </h1>
                  <p className="text-gray-500 text-center mb-8" data-id="element-3519">
                    Last step! Your information is secure.
                  </p>

                  <form onSubmit={handleSubmit} className="space-y-5" data-id="element-3520">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5" data-id="element-3521">
                      <div data-id="element-3522">
                        <label htmlFor="first_name" className="block text-sm font-bold text-gray-700 mb-1" data-id="element-3523">
                          First Name
                        </label>
                        <input type="text" id="first_name" name="first_name" required value={formData.first_name} onChange={handleInputChange} className="w-full h-14 px-4 bg-gray-50 border border-gray-300 rounded-xl text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#00529B] focus:border-transparent" placeholder="First Name" data-id="element-3524" />
                      </div>
                      <div data-id="element-3525">
                        <label htmlFor="last_name" className="block text-sm font-bold text-gray-700 mb-1" data-id="element-3526">
                          Last Name
                        </label>
                        <input type="text" id="last_name" name="last_name" required value={formData.last_name} onChange={handleInputChange} className="w-full h-14 px-4 bg-gray-50 border border-gray-300 rounded-xl text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#00529B] focus:border-transparent" placeholder="Last Name" data-id="element-3527" />
                      </div>
                    </div>

                    <div data-id="element-3528">
                      <label htmlFor="email" className="block text-sm font-bold text-gray-700 mb-1" data-id="element-3529">
                        Email Address
                      </label>
                      <input type="email" id="email" name="email" required value={formData.email} onChange={handleInputChange} className="w-full h-14 px-4 bg-gray-50 border border-gray-300 rounded-xl text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#00529B] focus:border-transparent" placeholder="email@example.com" data-id="element-3530" />
                    </div>

                    <div data-id="element-3531">
                      <label htmlFor="phone_1" className="block text-sm font-bold text-gray-700 mb-1" data-id="element-3532">
                        Primary Phone
                      </label>
                      <input type="tel" id="phone_1" name="phone_1" required value={formData.phone_1} onChange={handlePhoneChange} className="w-full h-14 px-4 bg-gray-50 border border-gray-300 rounded-xl text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#00529B] focus:border-transparent" placeholder="555-123-4567" maxLength={12} data-id="element-3533" />
                    </div>

                    <div className="pt-4" data-id="element-3534">
                      <label className="flex items-start gap-3 mb-4 cursor-pointer" data-id="element-3535">
                        <input type="checkbox" checked={consentChecked} onChange={e => setConsentChecked(e.target.checked)} className="mt-0.5 w-5 h-5 rounded border-gray-300 text-[#00529B] focus:ring-[#00529B] cursor-pointer flex-shrink-0" required data-id="element-3536" />
                        <span className="text-[11px] text-gray-500 leading-tight" data-id="element-3537">
                          By clicking the button, I provide my electronic
                          signature and express written consent to share my
                          contact information with FindServiceQuotes, including
                          their contractors and affiliates, and up to four{' '}
                          <a href="https://findservicequotes.io/possible-buyers" target="_blank" rel="noopener noreferrer" className="underline text-[#00529B]" data-id="element-3538">
                            home service companies
                          </a>{' '}
                          including Universal Windows Direct. I authorize them
                          to email, call, or text me for marketing purposes
                          using autodialer technology, AI generated or AI
                          assisted voice, or prerecorded voice messages on the
                          phone number I provided, even if listed on a DoNotCall
                          list. Consent is not a condition to purchase. I agree
                          to the{' '}
                          <a href="/terms-of-use" target="_blank" rel="noopener noreferrer" className="underline text-[#00529B]" data-id="element-3539">
                            Terms of Use
                          </a>{' '}
                          and{' '}
                          <a href="/privacy-policy" target="_blank" rel="noopener noreferrer" className="underline text-[#00529B]" data-id="element-3540">
                            Privacy Policy
                          </a>
                          .
                        </span>
                      </label>
                      <button type="submit" disabled={isSubmitting || !consentChecked} className="w-full h-16 bg-[#00529B] hover:bg-[#004080] text-white font-bold text-xl rounded-xl transition-colors flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed shadow-lg shadow-blue-900/30" data-id="element-3541">
                        {isSubmitting ? 'Processing...' : 'Get Your Free Quote'}
                      </button>
                    </div>
                  </form>
                </motion.div>}
            </AnimatePresence>
          </div>
        </div>
      </main>
    </div>;
}